import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { memberService } from '../../services/memberService';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { Input, Label, FormGroup, Select } from '../../components/Input';
import { Loader } from '../../components/Loader';
import { toast } from 'react-toastify';

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
`;

const Title = styled.h1`
  color: ${colors.primary};
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const SearchInput = styled.input`
  padding: 12px 20px;
  border: 2px solid ${colors.secondary};
  border-radius: 8px;
  font-size: 16px;
  flex: 1;
  min-width: 300px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(156, 89, 57, 0.1);
  }

  &::placeholder {
    color: ${colors.textLight};
  }

  @media (max-width: 768px) {
    min-width: 100%;
    font-size: 14px;
    padding: 10px 16px;
  }
`;

const SearchStats = styled.div`
  font-size: 14px;
  color: ${colors.textLight};
  font-weight: 500;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 50px 12px 20px;
  border: 2px solid ${colors.secondary};
  border-radius: 8px;
  font-size: 16px;
  background: white;
  color: ${colors.text};
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 150px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 20px center;
  background-size: 12px;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(156, 89, 57, 0.1);
  }

  @media (max-width: 768px) {
    min-width: 100%;
    font-size: 14px;
    padding: 10px 45px 10px 16px;
    background-position: right 16px center;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px ${colors.shadow};

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileCard = styled.div`
  display: none;
  background: white;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px ${colors.shadow};

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const MobileCardInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
`;

const MobileCardLabel = styled.span`
  font-size: 12px;
  color: ${colors.textLight};
  font-weight: 500;
`;

const MobileCardValue = styled.span`
  font-size: 14px;
  color: ${colors.text};
  font-weight: 400;
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Th = styled.th`
  background-color: ${colors.primary};
  color: white;
  padding: 15px;
  text-align: left;
  font-weight: 500;

  @media (max-width: 768px) {
    padding: 10px 8px;
  }

  @media (max-width: 480px) {
    padding: 8px 4px;
  }
`;

const Td = styled.td`
  padding: 15px;
  border-bottom: 1px solid ${colors.border};

  @media (max-width: 768px) {
    padding: 10px 8px;
  }

  @media (max-width: 480px) {
    padding: 8px 4px;
  }
`;

const Tr = styled.tr`
  &:hover {
    background-color: ${colors.background};
  }
`;

const Badge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => props.$active ? colors.success : colors.error};
  color: white;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
`;

const ProfilePhoto = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  cursor: pointer;
  border: 2px solid ${colors.primary};
`;

const PhotoPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
  font-weight: bold;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const FullImage = styled.img`
  width: 100%;
  max-width: 400px;
  border-radius: 12px;
  margin: 0 auto;
  display: block;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: ${colors.text};
  
  input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

export const AdminMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    definitionType: 'membership', // 'membership' or 'reservation'
    membershipStart: '',
    membershipEnd: '',
    reservationLimit: '',
    isActive: true
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await memberService.getAllMembers();
      setMembers(data);
    } catch (error) {
      toast.error('Üyeler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (member = null) => {
    if (member) {
      setSelectedMember(member);
      const remainingReservations =
        member.remainingReservations !== undefined
          ? member.remainingReservations
          : Math.max(
              (member.reservationLimit || 0) - (member.reservationsUsed || 0),
              0
            );

      setFormData({
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email,
        phone: member.phone || '',
        definitionType: member.definitionType || 'membership',
        membershipStart: member.membershipStart,
        membershipEnd: member.membershipEnd,
        reservationLimit:
          member.definitionType === 'reservation'
            ? String(remainingReservations)
            : '',
        isActive: member.isActive,
        password: ''
      });
    } else {
      setSelectedMember(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
        definitionType: 'membership',
        membershipStart: '',
        membershipEnd: '',
        reservationLimit: '',
        isActive: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMember(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Form verilerini temizle ve hazırla
      const submitData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        membershipStart: formData.membershipStart,
        membershipEnd: formData.membershipEnd,
        definitionType: formData.definitionType,
        reservationLimit:
          formData.definitionType === 'membership'
            ? 999
            : Math.max(0, Number(formData.reservationLimit) || 0),
        isActive: formData.isActive
      };


      if (selectedMember) {
        await memberService.updateMember(selectedMember.id, submitData);
        if (formData.password) {
          await memberService.updateMemberPassword(selectedMember.id, formData.password);
        }
        toast.success('Üye güncellendi');
      } else {
        await memberService.createMember(submitData);
        toast.success('Üye oluşturuldu');
      }
      handleCloseModal();
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu üyeyi silmek istediğinize emin misiniz?')) {
      try {
        await memberService.deleteMember(id);
        toast.success('Üye silindi');
        fetchMembers();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Silme işlemi başarısız');
      }
    }
  };

  const handlePhotoClick = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setPhotoModalOpen(true);
  };

  // Filter members based on search query and status
  const filteredMembers = members.filter(member => {
    // Status filter
    if (statusFilter === 'active' && !member.isActive) return false;
    if (statusFilter === 'inactive' && member.isActive) return false;
    
    // Search query filter
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
    const email = member.email.toLowerCase();
    const phone = (member.phone || '').toLowerCase();
    
    return fullName.includes(query) || 
           email.includes(query) || 
           phone.includes(query);
  });

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Header>
        <Title>Üye Yönetimi</Title>
        <Button onClick={() => handleOpenModal()}>+ Yeni Üye Ekle</Button>
      </Header>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="🔍 Üye adı, email veya telefon ile ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tümü</option>
          <option value="active">Aktif</option>
          <option value="inactive">Pasif</option>
        </FilterSelect>
        <SearchStats>
          {filteredMembers.length} üye gösteriliyor
          {(searchQuery || statusFilter !== 'all') && ` (${members.length} üyeden)`}
        </SearchStats>
      </SearchContainer>

      <Table>
        <thead>
          <tr>
            <Th>Fotoğraf</Th>
            <Th>Ad Soyad</Th>
            <Th>Email</Th>
            <Th>Telefon</Th>
            <Th>Kalan Gün</Th>
            <Th>Rezervasyon</Th>
            <Th>Durum</Th>
            <Th>İşlemler</Th>
          </tr>
        </thead>
        <tbody>
          {filteredMembers.map(member => (
            <Tr key={member.id}>
              <Td>
                {member.profilePhoto ? (
                  <ProfilePhoto 
                    src={member.profilePhoto} 
                    alt={`${member.firstName} ${member.lastName}`}
                    onClick={() => handlePhotoClick(member.profilePhoto)}
                  />
                ) : (
                  <PhotoPlaceholder>
                    {(() => {
                      const firstName = member.firstName || '';
                      const lastName = member.lastName || '';
                      const firstInitial = firstName.trim() ? firstName.trim().charAt(0).toUpperCase() : '';
                      const lastInitial = lastName.trim() ? lastName.trim().charAt(0).toUpperCase() : '';
                      return firstInitial + lastInitial || '??';
                    })()}
                  </PhotoPlaceholder>
                )}
              </Td>
              <Td>{member.firstName} {member.lastName}</Td>
              <Td>{member.email}</Td>
              <Td>{member.phone || '-'}</Td>
              <Td>
                {member.remainingDays} gün
              </Td>
              <Td>
                {member.definitionType === 'reservation' 
                  ? `${member.remainingReservations} / ${member.reservationLimit}`
                  : 'Üyeliği bitene kadar'
                }
              </Td>
              <Td>
                <Badge $active={member.isActive}>
                  {member.isActive ? 'Aktif' : 'Pasif'}
                </Badge>
              </Td>
              <Td>
                <Actions>
                  <Button size="small" onClick={() => handleOpenModal(member)}>
                    Düzenle
                  </Button>
                  <Button 
                    size="small" 
                    variant="danger" 
                    onClick={() => handleDelete(member.id)}
                  >
                    Sil
                  </Button>
                </Actions>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {/* Mobile Card Layout */}
      {filteredMembers.map(member => (
        <MobileCard key={member.id}>
          <MobileCardHeader>
            {member.profilePhoto ? (
              <ProfilePhoto 
                src={member.profilePhoto} 
                alt={`${member.firstName} ${member.lastName}`}
                onClick={() => handlePhotoClick(member.profilePhoto)}
                style={{ width: '50px', height: '50px' }}
              />
            ) : (
              <PhotoPlaceholder style={{ width: '50px', height: '50px', fontSize: '18px' }}>
                {(() => {
                  const firstName = member.firstName || '';
                  const lastName = member.lastName || '';
                  const firstInitial = firstName.trim() ? firstName.trim().charAt(0).toUpperCase() : '';
                  const lastInitial = lastName.trim() ? lastName.trim().charAt(0).toUpperCase() : '';
                  return firstInitial + lastInitial || '??';
                })()}
              </PhotoPlaceholder>
            )}
            <div>
              <h3 style={{ margin: 0, color: colors.primary, fontSize: '16px' }}>
                {member.firstName} {member.lastName}
              </h3>
              <Badge $active={member.isActive} style={{ marginTop: '4px' }}>
                {member.isActive ? 'Aktif' : 'Pasif'}
              </Badge>
            </div>
          </MobileCardHeader>
          
          <MobileCardInfo>
            <MobileCardLabel>Email:</MobileCardLabel>
            <MobileCardValue>{member.email}</MobileCardValue>
            
            <MobileCardLabel>Telefon:</MobileCardLabel>
            <MobileCardValue>{member.phone || '-'}</MobileCardValue>
            
            <MobileCardLabel>Kalan Gün:</MobileCardLabel>
            <MobileCardValue>{member.remainingDays} gün</MobileCardValue>
            
            <MobileCardLabel>Rezervasyon:</MobileCardLabel>
            <MobileCardValue>
              {member.definitionType === 'reservation' 
                ? `${member.remainingReservations} / ${member.reservationLimit}`
                : 'Üyeliği bitene kadar'
              }
            </MobileCardValue>
          </MobileCardInfo>
          
          <MobileCardActions>
            <Button size="small" onClick={() => handleOpenModal(member)}>
              Düzenle
            </Button>
            <Button 
              size="small" 
              variant="danger" 
              onClick={() => handleDelete(member.id)}
            >
              Sil
            </Button>
          </MobileCardActions>
        </MobileCard>
      ))}

      {filteredMembers.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: 'white',
          borderRadius: '12px',
          color: colors.textLight
        }}>
          <p style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</p>
          <h3 style={{ color: colors.text, marginBottom: '10px' }}>
            {searchQuery ? 'Sonuç Bulunamadı' : 'Henüz Üye Yok'}
          </h3>
          <p>
            {searchQuery 
              ? `"${searchQuery}" araması için üye bulunamadı. Farklı bir arama deneyin.`
              : 'Henüz sisteme eklenmiş üye bulunmuyor.'
            }
          </p>
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={selectedMember ? 'Üye Düzenle' : 'Yeni Üye Ekle'}
        maxWidth="600px"
      >
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Ad</Label>
            <Input
              value={formData.firstName}
              onChange={(e) => setFormData({...formData, firstName: e.target.value})}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Soyad</Label>
            <Input
              value={formData.lastName}
              onChange={(e) => setFormData({...formData, lastName: e.target.value})}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              autoComplete="off"
            />
          </FormGroup>

          <FormGroup>
            <Label>Şifre {selectedMember && '(Değiştirmek için doldurun)'}</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required={!selectedMember}
              placeholder={selectedMember ? 'Boş bırakın değişmeyecekse' : ''}
              autoComplete="new-password"
            />
          </FormGroup>

          <FormGroup>
            <Label>Telefon</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <Label>Tanımlama Türü</Label>
            <RadioGroup>
              <RadioLabel>
                <input
                  type="radio"
                  value="membership"
                  checked={formData.definitionType === 'membership'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      definitionType: e.target.value,
                      reservationLimit: ''
                    })
                  }
                />
                Üyelik Tarihi ile Tanımla
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  value="reservation"
                  checked={formData.definitionType === 'reservation'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      definitionType: e.target.value,
                      reservationLimit:
                        formData.reservationLimit && formData.reservationLimit !== ''
                          ? formData.reservationLimit
                          : '1'
                    })
                  }
                />
                Rezervasyon Hakkı ile Tanımla
              </RadioLabel>
            </RadioGroup>
          </FormGroup>

          <FormGroup>
            <Label>Üyelik Başlangıç</Label>
            <Input
              type="date"
              value={formData.membershipStart}
              onChange={(e) => setFormData({...formData, membershipStart: e.target.value})}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Üyelik Bitiş</Label>
            <Input
              type="date"
              value={formData.membershipEnd}
              onChange={(e) => setFormData({...formData, membershipEnd: e.target.value})}
              required
            />
          </FormGroup>

          {formData.definitionType === 'reservation' && (
            <FormGroup>
              <Label>Rezervasyon Hakkı</Label>
              <Input
                type="number"
                min="1"
                value={formData.reservationLimit}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    reservationLimit: e.target.value
                  })
                }
                required
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label>Durum</Label>
            <Select
              value={formData.isActive}
              onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}
            >
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </Select>
          </FormGroup>

          <ButtonGroup>
            <Button type="submit" fullWidth>
              {selectedMember ? 'Güncelle' : 'Oluştur'}
            </Button>
            <Button type="button" variant="secondary" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </ButtonGroup>
        </form>
      </Modal>

      <Modal
        isOpen={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        title="Profil Fotoğrafı"
      >
        <FullImage src={selectedPhoto} alt="Profil Fotoğrafı" />
      </Modal>
    </Container>
  );
};

